/**
 * BlueprintJobView — Tier 2 (background+polling) frontend component for
 * Operating Manual and Team Blueprint generations.
 *
 * Per /shared/V1_OM_TeamBlueprint_Background_Polling_Relock.md (locked
 * 2026-05-07).
 *
 * Replaces StreamingMarkdown for the two surfaces that exceed Netlify's
 * V2 streaming runtime cap. Implements the 4-item progress legibility
 * rubric required for Tier 2 SLA compliance:
 *
 *   1. Elapsed timer (live count-up since job start)
 *   2. Phase label (loading inputs / matchup synthesis / rendering / etc)
 *   3. Under-promised completion estimate ("typically <90s; we stay below 2 min for 95% of runs")
 *   4. Cancel button
 *
 * Lifecycle:
 *   - Parent passes a `start()` async function that calls /api/.../start
 *     and returns { job_id, estimated_total_seconds }.
 *   - Component polls /api/blueprint-job-status every 2s while the job
 *     is pending or running.
 *   - On status="complete": calls onComplete(blueprint_id) so parent can
 *     load the persisted blueprint and render OperatingManualPage /
 *     TeamBlueprintPage.
 *   - On status="failed" or "cancelled": surfaces a Watch Out callout
 *     with the error_message and a Try again button.
 *   - Cancel button: POSTs to /api/blueprint-job-cancel and stops polling.
 *
 * Tier 2 SLA observable here:
 *   - First poll <1s p95 — kicked off immediately on mount, no backoff
 *   - Time to result <2min p95 / <3min p99 — surfaced as the "typically
 *     under N seconds" affordance with visible elapsed timer for honesty.
 */

import { useEffect, useRef, useState } from "react";
import Callout from "./Callout.jsx";
import { parseJsonResponse } from "../lib/api-fetch.js";
import { diag } from "../lib/diag.js";

const POLL_INTERVAL_MS = 2000;

export default function BlueprintJobView({
  start,                  // async () => { ok, error?, job_id?, estimated_total_seconds? }
  bearerToken,
  onComplete,             // (blueprint_id, jobRow) => void — called when status flips to complete
  onRetry,                // () => void — optional; when provided, failure state renders a Regenerate button (#72)
  surfaceLabel = "Playbook",
}) {
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | starting | pending | running | complete | failed | cancelled | start_failed
  const [phase, setPhase] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [estimatedSeconds, setEstimatedSeconds] = useState(90);
  const [errorMessage, setErrorMessage] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const pollTimerRef = useRef(null);
  const tickTimerRef = useRef(null);

  // ── Kick off the job on mount ────────────────────────────────────────
  useEffect(() => {
    let active = true;
    setStatus("starting");
    (async () => {
      try {
        const result = await start();
        if (!active) return;
        if (!result.ok) {
          setStatus("start_failed");
          setErrorMessage(result.error || "Could not start the generation.");
          return;
        }
        setJobId(result.job_id);
        setEstimatedSeconds(result.estimated_total_seconds || 90);
        setStatus("pending");
      } catch (e) {
        if (!active) return;
        setStatus("start_failed");
        setErrorMessage(e.message || "Network error starting the generation.");
      }
    })();
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Poll the status endpoint while the job is in-flight ──────────────
  useEffect(() => {
    if (!jobId || !["pending", "running"].includes(status)) return;
    let active = true;

    let pollCount = 0;
    const poll = async () => {
      pollCount += 1;
      const myPollN = pollCount;
      diag("BlueprintJobView.poll.start", { poll_n: myPollN, job_id: jobId });
      try {
        const res = await fetch(
          `/.netlify/functions/blueprint-job-status?job_id=${encodeURIComponent(jobId)}`,
          { headers: { authorization: `Bearer ${bearerToken}` } },
        );
        const body = await parseJsonResponse(res);
        diag("BlueprintJobView.poll.end", {
          poll_n: myPollN,
          ok: res.ok,
          status: body?.status,
          phase: body?.phase,
          has_blueprint: !!body?.blueprint,
          blueprint_markdown_chars: body?.blueprint?.rendered_markdown?.length || 0,
        });
        if (!active) {
          diag("BlueprintJobView.poll.inactive-skipped", { poll_n: myPollN });
          return;
        }
        if (!res.ok) {
          return;
        }
        setPhase(body.phase || null);
        setElapsedSeconds(body.elapsed_seconds || 0);
        if (body.status && body.status !== status) {
          diag("BlueprintJobView.status.transition", {
            from: status, to: body.status, poll_n: myPollN,
          });
          setStatus(body.status);
        }
        if (body.status === "complete" && onComplete) {
          diag("BlueprintJobView.onComplete.fire", { poll_n: myPollN });
          onComplete(body.blueprint_id, body);
          diag("BlueprintJobView.onComplete.returned", { poll_n: myPollN });
        }
        if (body.status === "failed") {
          setErrorMessage(body.error_message || "Generation failed.");
        }
      } catch (e) {
        diag("BlueprintJobView.poll.error", { poll_n: myPollN, error: e.message });
      }
    };

    // Tier 2 SLA: first poll fires immediately (no initial wait).
    poll();
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      active = false;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [jobId, status, bearerToken, onComplete]);

  // ── Live elapsed-timer tick ──────────────────────────────────────────
  // The status endpoint also reports elapsed_seconds, but it only updates
  // every poll interval (2s) so the visible timer would jump in 2s steps.
  // A local 1Hz tick keeps the displayed counter smooth. Server-reported
  // elapsed wins on every poll so the two stay in sync.
  useEffect(() => {
    if (!["pending", "running"].includes(status)) {
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
      return;
    }
    tickTimerRef.current = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);
    return () => { if (tickTimerRef.current) clearInterval(tickTimerRef.current); };
  }, [status]);

  // ── Cancel button handler ────────────────────────────────────────────
  const cancelJob = async () => {
    if (!jobId || cancelling) return;
    setCancelling(true);
    try {
      await fetch("/.netlify/functions/blueprint-job-cancel", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${bearerToken}` },
        body: JSON.stringify({ job_id: jobId }),
      });
      setStatus("cancelled");
    } catch (e) {
      setErrorMessage(`Cancel failed: ${e.message}`);
    } finally {
      setCancelling(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────
  if (status === "complete") {
    // Parent renders the persisted blueprint via onComplete; show a
    // brief "Done" while the parent loads it.
    return <p style={mutedMonoStyle}>Generation complete — loading blueprint…</p>;
  }

  if (status === "start_failed" || status === "failed") {
    return (
      <Callout.WatchOut>
        <strong>Generation failed.</strong> {errorMessage || "Unknown error."}
        {onRetry ? (
          <>
            <p style={{ marginTop: 8, marginBottom: 12, fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: 12, color: "var(--ppr-color-text-muted)" }}>
              Click Regenerate to try again — most failures are transient and a retry succeeds. If the failure repeats, check your Profile data (DOB on file, observed tags, etc.) or contact support.
            </p>
            <button
              type="button"
              onClick={onRetry}
              data-testid="blueprint-job-view-regenerate"
              style={{
                fontFamily: "var(--ppr-typography-font-family-display)",
                fontSize: "var(--ppr-typography-font-size-sm)",
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                padding: "10px 18px",
                background: "var(--ppr-color-accent-navy)",
                color: "var(--ppr-color-text-inverse)",
                border: "1px solid var(--ppr-color-accent-navy)",
                borderRadius: "var(--ppr-radius-sm)",
                cursor: "pointer",
              }}
            >
              Regenerate
            </button>
          </>
        ) : (
          <p style={{ marginTop: 8, marginBottom: 0, fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: 12, color: "var(--ppr-color-text-muted)" }}>
            Refresh the page and click Generate again. If the failure repeats, check your Profile data (DOB on file, observed tags, etc.) or contact support.
          </p>
        )}
      </Callout.WatchOut>
    );
  }

  if (status === "cancelled") {
    return (
      <Callout.Tip>
        <strong>Generation cancelled.</strong> Click Generate {surfaceLabel} again whenever you're ready.
      </Callout.Tip>
    );
  }

  // pending or running: show progress
  return (
    <div data-testid="blueprint-job-view" data-status={status}>
      <div
        style={{
          padding: "20px 22px",
          border: "1px solid var(--ppr-color-border-default)",
          background: "var(--ppr-color-background-surface)",
        }}
      >
        <div
          style={{
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: "11px",
            letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
            textTransform: "uppercase",
            color: "var(--ppr-color-text-muted)",
            marginBottom: 8,
          }}
        >
          Generating {surfaceLabel}…
        </div>

        {/* Item 2: phase label */}
        <p
          style={{
            margin: "0 0 12px",
            fontFamily: "var(--ppr-typography-font-family-body)",
            fontSize: "var(--ppr-typography-font-size-base)",
            lineHeight: 1.55,
            color: "var(--ppr-color-text-primary)",
          }}
        >
          <strong>{phaseLabel(phase, status)}</strong>
        </p>

        {/* Item 1: elapsed timer + Item 3: under-promised estimate */}
        <p
          style={{
            margin: "0 0 16px",
            fontFamily: "var(--ppr-typography-font-family-mono)",
            fontSize: 13,
            color: "var(--ppr-color-text-muted)",
          }}
        >
          {formatElapsed(elapsedSeconds)} elapsed · typically under {estimatedSeconds}s · we stay below 2 minutes for 95% of runs
        </p>

        {/* Visual progress bar — hint at completion fraction without
            over-promising. Caps at 90% until status flips to complete so
            the bar never reaches the end while the user is still waiting. */}
        <div
          style={{
            height: 4,
            background: "var(--ppr-color-background-muted)",
            borderRadius: 2,
            overflow: "hidden",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.min(90, Math.round((elapsedSeconds / Math.max(1, estimatedSeconds)) * 90))}%`,
              background: "var(--ppr-color-accent-navy)",
              transition: "width 1s linear",
            }}
          />
        </div>

        {/* Item 4: cancel button */}
        <button
          onClick={cancelJob}
          disabled={cancelling}
          data-testid="blueprint-job-cancel"
          style={{
            padding: "8px 16px",
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: 11,
            letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
            textTransform: "uppercase",
            border: "1px solid var(--ppr-color-border-default)",
            background: "var(--ppr-color-background-surface)",
            color: "var(--ppr-color-text-secondary)",
            cursor: cancelling ? "wait" : "pointer",
          }}
        >
          {cancelling ? "Cancelling…" : "Cancel"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pure helpers — exported (named) for tests
// ---------------------------------------------------------------------------

export function phaseLabel(phase, status) {
  if (status === "starting") return "Starting…";
  if (status === "pending") return "Queued — picking up the job…";
  switch (phase) {
    case "loading_inputs":      return "Loading profile data…";
    case "matchup_synthesis":   return "Synthesizing the team matchup (Layer 2T)…";
    case "render":              return "Drafting the blueprint sections…";
    case "complete":            return "Done.";
    case "cancelled":           return "Cancelled.";
    case "failed":              return "Failed.";
    default:
      return "Working on it…";
  }
}

export function formatElapsed(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
}

const mutedMonoStyle = {
  fontFamily: "var(--ppr-typography-font-family-mono)",
  fontSize: 13,
  color: "var(--ppr-color-text-muted)",
};
